import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, Timer } from 'lucide-react';

interface BreathingExerciseProps {
  onComplete: () => void;
}

export function BreathingExercise({ onComplete }: BreathingExerciseProps) {
  const [duration, setDuration] = useState(5); // minutes
  const [isActive, setIsActive] = useState(false);
  const [currentPhase, setCurrentPhase] = useState<'inhale' | 'hold' | 'exhale'>('inhale');
  const [timeLeft, setTimeLeft] = useState(duration * 60);
  const [breathCount, setBreathCount] = useState(0);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (isActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            onComplete();
            return 0;
          }
          return prev - 1;
        });
        
        // Update breathing phase
        setCurrentPhase(prev => {
          if (prev === 'inhale') return 'hold';
          if (prev === 'hold') return 'exhale';
          setBreathCount(count => count + 1);
          return 'inhale';
        });
      }, 2000); // Each phase lasts 2 seconds
    }

    return () => clearInterval(interval);
  }, [isActive, timeLeft, onComplete]);

  const durations = [5, 10, 15, 20, 25, 30];

  return (
    <div className="space-y-8">
      {!isActive && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Timer className="w-4 h-4" />
            <span>Choose Duration</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            {durations.map((mins) => (
              <button
                key={mins}
                onClick={() => setDuration(mins)}
                className={`p-3 rounded-lg text-sm transition-colors ${
                  duration === mins ? 'mindful-timer' : 'bg-muted hover:bg-muted/70'
                }`}
              >
                {mins} min
              </button>
            ))}
          </div>
          <button
            onClick={() => setIsActive(true)}
            className="w-full mindful-timer p-4 rounded-xl text-primary font-medium relative group overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-shine" />
            <div className="relative flex items-center justify-center gap-2">
              Begin Practice
            </div>
          </button>
        </div>
      )}

      {isActive && (
        <div className="flex flex-col items-center justify-center space-y-8">
          <div className="relative">
            <motion.div 
              className="breathing-circle w-64 h-64 flex items-center justify-center"
              animate={{
                scale: currentPhase === 'inhale' ? 1.2 : 
                       currentPhase === 'hold' ? 1.2 : 0.8
              }}
              transition={{ duration: 2, ease: "easeInOut" }}
            >
              <div className="text-4xl font-bold mindful-gradient-text">
                {currentPhase === 'inhale' ? 'Inhale' :
                 currentPhase === 'hold' ? 'Hold' : 'Exhale'}
              </div>
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-purple-500/20 rounded-full blur-3xl" />
          </div>

          <div className="text-sm text-muted-foreground">
            Total Breaths: {breathCount}
          </div>

          <button
            onClick={() => setIsActive(false)}
            className="px-6 py-3 rounded-lg bg-muted text-muted-foreground hover:bg-muted/70 transition-colors"
          >
            End Session
          </button>
        </div>
      )}
    </div>
  );
}